import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingAPI } from '../../api';
import Layout from '../../components/Layout';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Edit, Trash2, ArrowRightLeft, Landmark } from 'lucide-react';
import { TransferBetweenAccountsSlideOver } from '../../components/banking';

const Banking = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('current_accounts'); // 'current_accounts', 'holding_accounts', 'credit_accounts', 'director_accounts', or 'transactions'
  
  // Account type filters for each tab
  const CURRENT_ACCOUNT_TYPES = ['current', 'savings', 'petty_cash', 'other'];
  const HOLDING_ACCOUNT_TYPES = ['payment_gateway'];
  const CREDIT_ACCOUNT_TYPES = ['credit_card', 'loan_account'];
  const DIRECTOR_ACCOUNT_TYPES = ['proprietor_drawings'];
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [dateFilters, setDateFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [transferSlideOverOpen, setTransferSlideOverOpen] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeTab, pagination.page, selectedAccountId, dateFilters]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bankingAPI.getAccounts({ include_inactive: true });
      setAccounts(data);
    } catch (err) {
      setError(err.message || 'Failed to load bank accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (selectedAccountId) {
        params.bank_account_id = selectedAccountId;
      }
      
      if (dateFilters.start_date) {
        params.start_date = dateFilters.start_date;
      }
      
      if (dateFilters.end_date) {
        params.end_date = dateFilters.end_date;
      }
      
      const response = await bankingAPI.getTransactions(params);
      setTransactions(response.transactions || []);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId, isSystemAccount) => {
    if (isSystemAccount) {
      alert('System accounts cannot be deleted');
      return;
    }
    
    if (!confirm('Are you sure you want to delete this account? This action cannot be undone.')) return;

    try {
      await bankingAPI.deleteAccount(accountId);
      fetchAccounts();
      if (selectedAccountId === accountId) {
        setSelectedAccountId('');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete account');
      console.error('Delete error:', err);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!confirm('Are you sure you want to delete this transaction?')) return;

    try {
      await bankingAPI.deleteTransaction(transactionId);
      fetchTransactions();
      fetchAccounts(); // Refresh balances
    } catch (err) {
      alert(err.message || 'Failed to delete transaction');
      console.error('Delete error:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getAccountTypeLabel = (type) => {
    const labels = {
      current: 'Current Account',
      savings: 'Savings Account',
      petty_cash: 'Petty Cash',
      proprietor_drawings: 'Proprietor Drawings',
      credit_card: 'Credit Card',
      payment_gateway: 'Payment Gateway',
      loan_account: 'Loan Account',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer: 'Transfer',
      manual: 'Manual'
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      deposit: 'text-green-600 dark:text-green-400',
      withdrawal: 'text-red-600 dark:text-red-400',
      transfer: 'text-blue-600 dark:text-blue-400',
      manual: 'text-gray-600 dark:text-gray-400'
    };
    return colors[type] || 'text-gray-600 dark:text-gray-400';
  };

  const handleTransferSuccess = () => {
    fetchAccounts();
    if (activeTab === 'transactions') fetchTransactions();
  };

  return (
    <TooltipProvider>
      <Layout>
      <TransferBetweenAccountsSlideOver
        open={transferSlideOverOpen}
        onOpenChange={setTransferSlideOverOpen}
        onSuccess={handleTransferSuccess}
      />
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Banking
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage bank accounts and transactions
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions card - full width above tabs */}
        <Card className="mb-6">
          <div className="px-6 py-4 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tenant/banking/accounts/new')}
            >
              + New Account
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTransferSlideOverOpen(true)}
              className="inline-flex items-center gap-1.5"
            >
              <ArrowRightLeft className="h-4 w-4 shrink-0" />
              Transfer between accounts
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tenant/banking/reconciliation')}
            >
              Reconciliation
            </Button>
          </div>
        </Card>

        {/* Tabs */}
        <Card className="mb-6">
          <div className="border-b border-[var(--ds-border-divider)]">
            <nav className="-mb-px flex">
              <button
                onClick={() => {
                  setActiveTab('current_accounts');
                  setSelectedAccountId('');
                  setDateFilters({ start_date: '', end_date: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`py-4 px-6 border-b-2 font-medium text-xs ${
                  activeTab === 'current_accounts'
                    ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-[var(--ds-border-subtle)]'
                }`}
              >
                Current Accounts
              </button>
              <button
                onClick={() => {
                  setActiveTab('holding_accounts');
                  setSelectedAccountId('');
                  setDateFilters({ start_date: '', end_date: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`py-4 px-6 border-b-2 font-medium text-xs ${
                  activeTab === 'holding_accounts'
                    ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-[var(--ds-border-subtle)]'
                }`}
              >
                Holding Accounts
              </button>
              <button
                onClick={() => {
                  setActiveTab('credit_accounts');
                  setSelectedAccountId('');
                  setDateFilters({ start_date: '', end_date: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`py-4 px-6 border-b-2 font-medium text-xs ${
                  activeTab === 'credit_accounts'
                    ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-[var(--ds-border-subtle)]'
                }`}
              >
                Credit Accounts
              </button>
              <button
                onClick={() => {
                  setActiveTab('director_accounts');
                  setSelectedAccountId('');
                  setDateFilters({ start_date: '', end_date: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`py-4 px-6 border-b-2 font-medium text-xs ${
                  activeTab === 'director_accounts'
                    ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-[var(--ds-border-subtle)]'
                }`}
              >
                Director Accounts
              </button>
              <button
                onClick={() => {
                  setActiveTab('transactions');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`py-4 px-6 border-b-2 font-medium text-xs ${
                  activeTab === 'transactions'
                    ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-[var(--ds-border-subtle)]'
                }`}
              >
                Recent Transactions
              </button>
            </nav>
          </div>
          {/* Tab description */}
          <div className="px-6 py-4">
            <p className="text-meta text-[var(--ds-text-secondary)] text-gray-600 dark:text-gray-400 leading-relaxed">
              {activeTab === 'current_accounts' &&
                'This section is used for business current and savings account tracking. It records expenses and income and feeds into Profit & Loss reports as well as Tax & VAT return reports.'}
              {activeTab === 'holding_accounts' &&
                'This section should be used for holding accounts such as payment gateways (e.g. Stripe) where funds sit for a short period before being transferred out to current accounts.'}
              {activeTab === 'credit_accounts' &&
                'This section helps you track business finance credit accounts—not suppliers or wholesalers, but business loans, credit cards and mortgages (use Loan Account type).'}
              {activeTab === 'director_accounts' &&
                'Use this to track payments to directors or owners. It helps with tax returns and financial statements.'}
              {activeTab === 'transactions' &&
                'This shows an overview of recent transactions across accounts. For detailed transactions per account, open the individual account.'}
            </p>
          </div>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
            {error}
          </div>
        )}

        {/* Current Accounts Tab */}
        {activeTab === 'current_accounts' && (() => {
          const filteredAccounts = accounts.filter(acc => CURRENT_ACCOUNT_TYPES.includes(acc.account_type));
          return (
          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading accounts...</p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
                <p>No current accounts found</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/banking/accounts/new')}
                  className="mt-4"
                >
                  Create your first account
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bank Details</TableHead>
                    <TableHead>Opening Balance</TableHead>
                    <TableHead>Current Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/tenant/banking/accounts/${account.id}/transactions`)}
                          className="text-xs font-medium text-[var(--ds-accent-primary)] hover:underline text-left"
                        >
                          {account.account_name}
                        </button>
                        {account.is_system_account && (
                          <div className="text-xs text-[var(--ds-text-muted)]">System Account</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {getAccountTypeLabel(account.account_type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {account.bank_name || '-'}
                        </div>
                        {(account.account_number || account.sort_code) && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {account.sort_code || ''} {account.account_number || ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100 tabular-nums">
                          {formatCurrency(account.opening_balance)}
                        </div>
                        {account.opening_balance_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(account.opening_balance_date)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`text-xs font-medium tabular-nums ${
                          parseFloat(account.current_balance || 0) >= 0 
                            ? 'text-gray-900 dark:text-gray-100' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(account.current_balance)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={account.is_active ? 'success' : 'neutral'} className="text-xs">
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end space-x-2">
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/banking/accounts/${account.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          {!account.is_system_account && (
                            <Tooltip content="Delete">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleDeleteAccount(account.id, account.is_system_account)}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
          );
        })()}

        {/* Holding Accounts Tab */}
        {activeTab === 'holding_accounts' && (() => {
          const filteredAccounts = accounts.filter(acc => HOLDING_ACCOUNT_TYPES.includes(acc.account_type));
          return (
          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading accounts...</p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Holding Accounts</h3>
                <p className="mb-4">Manage payment gateways and other holding accounts.</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/banking/accounts/new')}
                >
                  Add Holding Account
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Provider Details</TableHead>
                    <TableHead>Opening Balance</TableHead>
                    <TableHead>Current Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/tenant/banking/accounts/${account.id}/transactions`)}
                          className="text-xs font-medium text-[var(--ds-accent-primary)] hover:underline text-left"
                        >
                          {account.account_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {getAccountTypeLabel(account.account_type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {account.bank_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100 tabular-nums">
                          {formatCurrency(account.opening_balance)}
                        </div>
                        {account.opening_balance_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(account.opening_balance_date)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`text-xs font-medium tabular-nums ${
                          parseFloat(account.current_balance || 0) >= 0 
                            ? 'text-gray-900 dark:text-gray-100' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(account.current_balance)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={account.is_active ? 'success' : 'neutral'} className="text-xs">
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end space-x-2">
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/banking/accounts/${account.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleDeleteAccount(account.id, account.is_system_account)}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
          );
        })()}

        {/* Credit Accounts Tab */}
        {activeTab === 'credit_accounts' && (() => {
          const filteredAccounts = accounts.filter(acc => CREDIT_ACCOUNT_TYPES.includes(acc.account_type));
          return (
          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading accounts...</p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Credit Accounts</h3>
                <p className="mb-4">Manage credit cards and loan accounts.</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/banking/accounts/new')}
                >
                  Add Credit Account
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Provider Details</TableHead>
                    <TableHead>Credit Limit</TableHead>
                    <TableHead>Current Balance</TableHead>
                    <TableHead>Credit Remaining</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/tenant/banking/accounts/${account.id}/transactions`)}
                          className="text-xs font-medium text-[var(--ds-accent-primary)] hover:underline text-left"
                        >
                          {account.account_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {getAccountTypeLabel(account.account_type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {account.bank_name || '-'}
                        </div>
                        {account.account_number && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {account.account_number}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100 tabular-nums">
                          {account.credit_limit ? formatCurrency(account.credit_limit) : '-'}
                        </div>
                        {!account.credit_limit && account.opening_balance_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Opening: {formatDate(account.opening_balance_date)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`text-xs font-medium tabular-nums ${
                          parseFloat(account.current_balance || 0) <= 0 
                            ? 'text-gray-900 dark:text-gray-100' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(account.current_balance)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const creditLimit = parseFloat(account.credit_limit || 0);
                          const balanceAbs = Math.abs(parseFloat(account.current_balance || 0));
                          const creditRemaining = creditLimit - balanceAbs;
                          return (
                            <div className={`text-xs font-medium tabular-nums ${
                              account.credit_limit 
                                ? (creditRemaining >= 0
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-600 dark:text-red-400')
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {account.credit_limit 
                                ? formatCurrency(creditRemaining)
                                : '-'
                              }
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={account.is_active ? 'success' : 'neutral'} className="text-xs">
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end space-x-2">
                          {account.account_type === 'loan_account' && (
                            <Tooltip content="Loan setup">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/banking/accounts/${account.id}/loan`)}
                                className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                              >
                                <Landmark className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/banking/accounts/${account.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleDeleteAccount(account.id, account.is_system_account)}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
          );
        })()}

        {/* Director Accounts Tab */}
        {activeTab === 'director_accounts' && (() => {
          const filteredAccounts = accounts.filter(acc => DIRECTOR_ACCOUNT_TYPES.includes(acc.account_type));
          return (
          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading accounts...</p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Director Accounts</h3>
                <p className="mb-4">Track proprietor drawings and other director-related financial accounts.</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/banking/accounts/new')}
                >
                  Add Director Account
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Opening Balance</TableHead>
                    <TableHead>Current Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/tenant/banking/accounts/${account.id}/transactions`)}
                          className="text-xs font-medium text-[var(--ds-accent-primary)] hover:underline text-left"
                        >
                          {account.account_name}
                        </button>
                        {account.is_system_account && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">System Account</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {getAccountTypeLabel(account.account_type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100">
                          {account.notes || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-900 dark:text-gray-100 tabular-nums">
                          {formatCurrency(account.opening_balance)}
                        </div>
                        {account.opening_balance_date && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(account.opening_balance_date)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`text-xs font-medium tabular-nums ${
                          parseFloat(account.current_balance || 0) >= 0 
                            ? 'text-gray-900 dark:text-gray-100' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(account.current_balance)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={account.is_active ? 'success' : 'neutral'} className="text-xs">
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end space-x-2">
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/banking/accounts/${account.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          {!account.is_system_account && (
                            <Tooltip content="Delete">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleDeleteAccount(account.id, account.is_system_account)}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
          );
        })()}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <>
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <FormLabel htmlFor="account-filter">Account</FormLabel>
                  <Select
                    id="account-filter"
                    value={selectedAccountId}
                    onChange={(e) => {
                      setSelectedAccountId(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="h-8"
                  >
                    <option value="">All Accounts</option>
                    {accounts.filter(acc => acc.is_active).map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel htmlFor="start-date-filter">Start Date</FormLabel>
                  <Input
                    id="start-date-filter"
                    type="date"
                    value={dateFilters.start_date}
                    onChange={(e) => {
                      setDateFilters(prev => ({ ...prev, start_date: e.target.value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <FormLabel htmlFor="end-date-filter">End Date</FormLabel>
                  <Input
                    id="end-date-filter"
                    type="date"
                    value={dateFilters.end_date}
                    onChange={(e) => {
                      setDateFilters(prev => ({ ...prev, end_date: e.target.value }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="h-8"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedAccountId('');
                      setDateFilters({ start_date: '', end_date: '' });
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="w-full"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card className="overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading transactions...</p>
                </div>
              ) : transactions.length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
                  <p>No transactions found</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/tenant/banking/transactions/new')}
                    className="mt-4"
                  >
                    Create your first transaction
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow isHeader>
                        <TableHead>Date</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Linked To</TableHead>
                        <TableHead align="right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <div className="text-xs text-gray-900 dark:text-gray-100">
                              {formatDate(transaction.transaction_date)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-900 dark:text-gray-100">
                              {transaction.account_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="neutral" className="text-xs">
                              {getTransactionTypeLabel(transaction.transaction_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-900 dark:text-gray-100">
                              {transaction.description || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {transaction.reference || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={`text-xs font-medium tabular-nums ${
                              transaction.transaction_type === 'deposit' || 
                              (transaction.transaction_type === 'transfer' && transaction.linked_transfer_transaction_id)
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {transaction.transaction_type === 'deposit' || 
                               (transaction.transaction_type === 'transfer' && transaction.linked_transfer_transaction_id)
                                ? '+' : '-'}
                              {formatCurrency(transaction.amount)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {transaction.invoice_num && (
                                <div>Invoice: {transaction.invoice_num}</div>
                              )}
                              {transaction.purchase_num && (
                                <div>Purchase: {transaction.purchase_num}</div>
                              )}
                              {transaction.linked_transfer_transaction_id && (
                                <div>Transfer</div>
                              )}
                              {!transaction.invoice_num && !transaction.purchase_num && !transaction.linked_transfer_transaction_id && '-'}
                            </div>
                          </TableCell>
                          <TableCell align="right">
                            <div className="flex items-center justify-end space-x-2">
                              <Tooltip content="Edit">
                                <Button
                                  variant="icon"
                                  size="sm"
                                  onClick={() => navigate(`/tenant/banking/transactions/${transaction.id}`)}
                                  className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </Tooltip>
                              {!transaction.linked_invoice_payment_id && !transaction.linked_purchase_id && (
                                <Tooltip content="Delete">
                                  <Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handleDeleteTransaction(transaction.id)}
                                    className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <Card>
                      <CardContent className="px-4 py-3 sm:px-6 border-t border-[var(--ds-border-divider)]">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                          <span className="font-medium">{pagination.totalPages}</span>
                          {' '}({pagination.total} total transactions)
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
    </TooltipProvider>
  );
};

export default Banking;

